import React from 'react';
import {CROP_COLORS} from '../../../src/constants/colors';
import {
  AD1_AD2_SOIL,
  ALREADY_DAMAGED,
  CROP_ALREADY_PLANTED,
  CROP_IRRIGATED,
  FIRST_OR_SECOND_YEAR_HARVEST,
  OTHER_INSURANCE,
  PLANTING_SYSTEM,
  PREVIOUS_CULTURE,
  SAME_CULTURE_ENTIRE_AREA,
  SAO_PAULO_STATE_CODE,
  TOTAL_AREA_INSURED,
  ZONING_RECOMMENDATIONS,
  formatBraCurrency,
  formatBraDocumentNumber,
  formatBraNumber,
  formatBraPhoneNumber,
  formatBraZipcode,
  getPaymentDays,
} from '../../../src/custom/mga';
import {FarmCustomColumns, HarvestCustomColumns, PlantingType, ProductType} from '../../../src/models/CustomColumns';
import {SoilType} from '../../../src/models/types';
import {PolicyReportPackage} from '../../../src/report/report-types';
import {convertYield} from '../../../src/selectors/units';
import {addDaysToDate} from '../../../src/util/date-util';
import {
  calculatePaymentSlipAmount,
  calculatePremiumDetails,
  calculateSumInsuredPerArea,
} from '../../../src/util/policy-calculations';
import {FieldShape} from '../components/FieldShape';
import './AxaPolicyReport.css';
import {ReportApis} from './StandaloneReport';

function formatSoilType(soilType: null | undefined | SoilType) {
  if (soilType === 'clay') {
    return 'argiloso';
  } else if (soilType === 'sandy') {
    return 'arenoso';
  } else if (soilType === 'average') {
    return 'médio';
  }
  return '';
}

function formatProductType(productType: null | undefined | ProductType) {
  switch (productType) {
    case 'productivity':
      return 'Produtividade';
    case 'costing':
      return 'Custeio';
    default:
      return null;
  }
}

function formatQuestionAnswer(answer: null | undefined | boolean) {
  return answer ? 'Sim' : 'Não';
}

function formatPlantingType(plantingType: null | undefined | PlantingType) {
  switch (plantingType) {
    case 'direct':
      return 'direto';
    case 'conventional':
      return 'convencional';
    default:
      return plantingType;
  }
}

function getAxaContactsSection() {
  return (
    <>
      <h3>Central de Atendimento AXA</h3>
      <div className="grid four-columns">
        <div>0800 292 4357</div>
        <div>0800 021 1600</div>
        <div>axa.com.br</div>
        <div>consumidor.gov.br</div>
        <div>SAC AXA Help</div>
        <div>Ouvidoria</div>
        <div>Website AXA</div>
        <div>Atendimento ao consumidor</div>
      </div>
    </>
  );
}

function getEmissionDate(emissionDate: Date) {
  // This report is specific for AXA, so we can hardcode the timezone.
  const dateFormatter = new Intl.DateTimeFormat('pt-BR', {timeZone: 'America/Sao_Paulo'});
  const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
    hour12: false,
  });
  return (
    <>
      {dateFormatter.format(emissionDate)} às {timeFormatter.format(emissionDate)}h
    </>
  );
}

export const AxaPolicyReport: React.FC<PolicyReportPackage & ReportApis> = props => {
  const dateFormatter = new Intl.DateTimeFormat('pt-BR');
  const policyData = props.policyData;
  const policy = policyData.policy;
  const farm = policyData.farm;
  const farmLevelHarvest = policyData.farmLevelHarvest;
  const fieldLevelHarvests = policyData.fieldHarvests;
  const fields = policyData.fields;
  const policyCustomColumns = policyData.policyCustomColumns;
  const farmCustomColumns = new FarmCustomColumns(farm.custom_columns);
  const farmLevelHarvestCustomColumns = new HarvestCustomColumns(policyData.farmLevelHarvest.custom_columns);
  const policyExternalId = policyCustomColumns.mga.axa_policy_id;
  const fieldIndex = Object.fromEntries(fields.map(field => [field.field_id, field]));
  const documentType = policyExternalId ? 'Apólice' : 'Proposta';
  const emissionDate = new Date(policyExternalId ? policy.updated_at : policy.added_on);
  return (
    <div className="policy-report-container">
      <div className="policy-report-title">
        <div className="title">{documentType} DE SEGUROS</div>
        <div className="subtitle">AXA SEGURO AGRÍCOLA</div>
      </div>
      <div className="mb-20">
        <h3>Dados do Seguro</h3>
        <div className="grid four-columns four-columns-stripes emphasized-stripes">
          <div>Data Emissão da {documentType}</div>
          <div>{getEmissionDate(emissionDate)}</div>
          <div>Início de vigência</div>
          <div>
            {policyCustomColumns.valid_from
              ? `Das 24h de ${dateFormatter.format(new Date(policyCustomColumns.valid_from))}`
              : null}
          </div>
          <div>Versão</div>
          {/* The version is hardcoded based on the customer feedback on 2025-05-19. */}
          <div>{policyCustomColumns.mga.product_type === 'costing' ? '04' : '05'}/2025</div>
          <div>Fim de vigência</div>
          <div>
            {policyCustomColumns.valid_to
              ? `Até 24h de ${dateFormatter.format(new Date(policyCustomColumns.valid_to))}`
              : null}
          </div>
          <div>Proposta</div>
          <div>{policy.policy_number}</div>
          <div>Tipo de vigência</div>
          {/* Number of days is most likely hardcoded based on the discussion with the customer. */}
          <div>180 dias</div>
          <div>Apólice</div>
          <div>{policyExternalId}</div>
          <div>Cultura</div>
          <div>{policyData.cropId}</div>
          <div>Ramo</div>
          {/* Ramo is most likely hardcoded based on the discussion with the customer. */}
          <div>11.11 – Seguro Agrícola</div>
          <div>Tipo de Seguro</div>
          <div>{formatProductType(policyCustomColumns.mga.product_type)}</div>
          <div>Subvenção Federal</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.federal_subsidy_percent)}</div>
          <div>NC (%)</div>
          <div>{farmLevelHarvest.insured_percent}</div>
          <div>Subvenção Estadual</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.state_subsidy_percent)}</div>
          <div>Subvenção Municipal</div>
          {/* Municipality subsidies are done manually so the value here is always false. */}
          <div>Não</div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Dados do Segurado</h3>
        <div className="grid four-columns four-columns-stripes">
          <div>Segurado</div>
          <div>{policyCustomColumns.insured.name}</div>
          <div>CNPJ/CPF</div>
          <div>
            {formatBraDocumentNumber(policyCustomColumns.insured.external_id, policyCustomColumns.insured.type)}
          </div>
          <div>Nome Social do Segurado</div>
          <div>{policyCustomColumns.insured.alternative_name}</div>
          <div>Profissão/Ocupação</div>
          {/* Profissao/Ocupacao is most likely hardcoded based on the discussion with the customer. */}
          <div>Produtor Rural</div>
          <div>Endereço</div>
          <div>{policyCustomColumns.insured.address.street}</div>
          <div>Número</div>
          <div>{policyCustomColumns.insured.address.number}</div>
          <div>Complemento</div>
          <div>{policyCustomColumns.insured.address.additional_information}</div>
          <div>Bairro</div>
          <div>{policyCustomColumns.insured.address.neighborhood}</div>
          <div>Cidade</div>
          <div>{policyCustomColumns.insured.address.city}</div>
          <div>UF</div>
          <div>{policyCustomColumns.insured.address.state}</div>
          <div>CEP</div>
          <div>{formatBraZipcode(policyCustomColumns.insured.address.zip_code)}</div>
          <div className="merge-bottom-border">E-mail</div>
          <div className="merge-bottom-border">{policyCustomColumns.insured.email}</div>
          <div>Telefone com DDD</div>
          <div>{formatBraPhoneNumber(policyCustomColumns.insured.phone)}</div>
          <div className="merge-top-border"></div>
          <div className="merge-top-border"></div>
        </div>
      </div>
      <div className="mb-20">
        {/* Dados da Segurardora is most likely hardcoded based on the discussion with the customer. */}
        <h3>Dados da Seguradora</h3>
        <div className="grid four-columns four-columns-stripes">
          <div>Seguradora</div>
          <div>AXA Seguros S/A</div>
          <div className="merge-bottom-border">Registro SUSEP</div>
          <div className="merge-bottom-border">0285-2</div>
          <div>CNPJ</div>
          <div>19.323.190/0001-06</div>
          <div className="merge-top-border"></div>
          <div className="merge-top-border"></div>
        </div>
      </div>
      <div className="mb-20">
        {/* Representante de Seguro is most likely hardcoded based on the discussion with the customer. */}
        <h3>Representante de Seguro</h3>
        <div className="grid four-columns four-columns-stripes">
          <div>Nome/Razão Social</div>
          <div>Seguros Brasileira Rural</div>
          <div className="merge-bottom-border">Participação sobre o Prêmio Total</div>
          <div className="merge-bottom-border">6%</div>
          <div>CNPJ</div>
          <div>48.849.229/0001-21</div>
          <div className="merge-top-border"></div>
          <div className="merge-top-border"></div>
        </div>
        <div className="description">
          * Informamos que o Contrato de Representação em Seguros firmado entre a Seguradora e o Representante de
          Seguros, prevê a possibilidade de reversão de parte do resultado operacional positivo ao Representante. Na
          ocasião, tal reversão será paga diretamente ao Representante de Seguros.
        </div>
      </div>
      <div className="gt-report-page-separator-before" />
      <div className="gt-report-page-separator-after" />
      {policyCustomColumns.brokers.map((broker, index) => {
        return (
          <div className="mb-20">
            <h3>Dados do Corretor</h3>
            <div className="grid four-columns four-columns-stripes">
              <div>Nome/Razão Social</div>
              <div>{broker.entity.name}</div>
              <div className="merge-left-border"></div>
              <div className="merge-left-border"></div>
              <div>CNPJ</div>
              <div>{formatBraDocumentNumber(broker.entity.external_id, broker.entity.type)}</div>
              <div>Sucursal</div>
              <div>{broker.entity.address?.city}</div>
              <div>Número na SUSEP</div>
              <div>{broker.registration_number}</div>
              <div>E-mail</div>
              <div>{broker.entity.email}</div>
            </div>
          </div>
        );
      })}
      <div className="mb-20">
        <h3>Resumo do Risco</h3>
        <div className="grid four-columns four-columns-stripes">
          <div>Cultura</div>
          <div>{policyData.cropId}</div>
          <div>Área Total (ha)</div>
          <div>{formatBraNumber(policyData.totalArea, false, true)}</div>
          <div>Total de itens</div>
          <div>{fieldLevelHarvests.length}</div>
          <div>Nome da propriedade</div>
          <div>{farm.farm_name}</div>
          <div>Endereço do Risco</div>
          <div>{farmCustomColumns.address?.street}</div>
          <div>Nº</div>
          <div>{farmCustomColumns.address?.number ?? 's/n'}</div>
          <div>Complemento</div>
          <div>{farmCustomColumns.address?.additional_information}</div>
          <div>Cidade</div>
          <div>{farmCustomColumns.address?.city}</div>
          <div>UF</div>
          <div>{farmCustomColumns.address?.state}</div>
          <div className="merge-bottom-border">CEP</div>
          <div className="merge-bottom-border">{formatBraZipcode(farmCustomColumns.address?.zip_code)}</div>
          <div>Valor do Risco</div>
          <div>{formatBraCurrency(policyData.basicSumInsured + policyData.replantingSumInsured)}</div>
          <div className="merge-top-border"></div>
          <div className="merge-top-border"></div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Prêmio (Preço do Seguro)</h3>
        <div className="grid two-columns two-columns-stripes">
          <div>Preço do Seguro Líquido</div>
          <div>{formatBraCurrency(policyData.totalPremium)}</div>
          <div>Valor do IOF</div>
          <div>Isento</div>
          <div>Preço do Seguro Total</div>
          <div>{formatBraCurrency(policyData.totalPremium)}</div>
          <div>Preço do Seguro para o Segurado</div>
          <div>{formatBraCurrency(policyData.premiumPaidByInsured)}</div>
          <div>Preço do Seguro referente a Subvenção Federal</div>
          <div>{formatBraCurrency(policyData.federalSubsidy)}</div>
          <div>Preço do Seguro referente a Subvenção Estadual</div>
          <div>{formatBraCurrency(policyData.stateSubsidy)}</div>
          <div>Adicional de Fracionamento 2% ao mês (Moeda Real)</div>
          <div>{formatBraCurrency(0)}</div>
          <div>Condições de Pagamento</div>
          <div>
            1
            {policyCustomColumns.mga.installment_count && policyCustomColumns.mga.installment_count > 1
              ? ' +' + (policyCustomColumns.mga.installment_count - 1)
              : ''}{' '}
            BOLETO
          </div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Vencimento das Parcelas</h3>
        <div className="grid three-columns">
          <div>Parcela Nº</div>
          <div>Vencimento</div>
          <div>Valor</div>
          {Array.from({length: policyCustomColumns.mga.installment_count ?? 1}).map((_, index) => (
            <>
              <div>
                <span className="bold">{index + 1}</span> Segurado
              </div>
              <div>{dateFormatter.format(addDaysToDate(new Date(policy.updated_at), getPaymentDays(index)))}</div>
              <div>
                {formatBraCurrency(
                  calculatePaymentSlipAmount(
                    index,
                    policyData.premiumPaidByInsured,
                    policyCustomColumns.mga.installment_count ?? 1,
                  ),
                )}
              </div>
            </>
          ))}
          <div>
            <span className="bold">{(policyCustomColumns.mga.installment_count ?? 1) + 1}</span> Subvenção Federal
          </div>
          <div></div>
          <div>{formatBraCurrency(policyData.federalSubsidy)}</div>

          <div>
            <span className="bold">{(policyCustomColumns.mga.installment_count ?? 1) + 2}</span> Subvenção Estadual
          </div>
          <div></div>
          <div>{formatBraCurrency(policyData.stateSubsidy)}</div>

          {/* Municipality subsidies are not supported. */}
          <div>
            <span className="bold">{(policyCustomColumns.mga.installment_count ?? 1) + 3}</span> Subvenção Municipal
          </div>
          <div></div>
          <div>{formatBraCurrency(0)}</div>
        </div>
        <p className="description">
          Conforme Lei n°12.741 de 08 de Dezembro de 2012 a AXA Seguros S.A. vem informar a seus aos seus segurados a
          alíquota de 0,65% de PIS/PASEP e de 4% de COFINS sobre os prêmios de seguros, deduzidos do estabelecido em
          legislação específica
        </p>
      </div>
      <div className="gt-report-page-separator-before" />
      <div className="gt-report-page-separator-after" />

      {policyExternalId && (
        <>
          <div className="mb-20">
            <div className="plain-grid two-columns-extra">
              <div>
                <h3 className="emphasized">Olá</h3>
                <p>Antes de iniciarmos, nós da AXA precisamos te explicar algumas coisas, tudo bem?</p>
                <p>
                  Esta é a <span className="emphasized">Apólice do seu Seguro</span>, contratada de acordo com a
                  Proposta descrita na primeira página deste documento. Essa apólice irá trazer alguns detalhes
                  importantes e informações específicas do seu Seguro, como Limite da Indenização, valor do Preço de
                  Seguro e da POS (Participação Obrigatória do Segurado), etc.
                </p>
                <p>
                  O documento completo com todas as regras e obrigações é aquele que chamamos de{' '}
                  <span className="emphasized">Condições Contratuais</span> e pode ser acessado por este QR Code ao
                  lado. É muito importante que você leia o documento completo para entender o que está coberto pelo
                  seguro e seus direitos e deveres como Segurado.
                </p>
                <p>
                  Importante ressaltar ainda que as Condições Contratuais preveem as regras de todas as coberturas
                  oferecidas pela AXA neste Seguro Agrícola, mas no seu caso específico só estarão inclusas no seu
                  Seguro as coberturas contratadas nesta Apólice.
                </p>
              </div>
              <div className="gt-flex-card">
                <span>Condições Gerais do Seguro Agrícola</span>
                <img src="/mga/policy-qr-code.png" />
              </div>
            </div>
          </div>
          <div className="mb-20">{getAxaContactsSection()}</div>
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
        </>
      )}
      <div className="mb-20">
        <h3>Questionário de Avaliação do Risco</h3>
        <div className="grid two-columns two-columns-stripes emphasized-stripes">
          <div>{PREVIOUS_CULTURE}</div>
          <div>{policyCustomColumns.mga.previous_crop}</div>
          <div>{PLANTING_SYSTEM}</div>
          <div>{formatPlantingType(policyCustomColumns.mga.planting_type)}</div>
          <div>{CROP_ALREADY_PLANTED}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.crop_already_planted)}</div>
          <div>{ZONING_RECOMMENDATIONS}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.aware_of_zoagro_mapa)}</div>
          <div>{SAME_CULTURE_ENTIRE_AREA}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.same_crop_whole_area)}</div>
          <div>{OTHER_INSURANCE}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.preexisting_policy_for_same_area)}</div>
          <div>{TOTAL_AREA_INSURED}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.total_planted_area_insured)}</div>
          <div>{FIRST_OR_SECOND_YEAR_HARVEST}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.first_or_second_year)}</div>
          <div>{ALREADY_DAMAGED}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.crop_already_damaged)}</div>
          <div>{AD1_AD2_SOIL}</div>
          <div>{formatQuestionAnswer(!!policyCustomColumns.mga.predominantly_ad1_ad2_soil)}</div>
          <div>{CROP_IRRIGATED}</div>
          <div>{formatQuestionAnswer(farmLevelHarvest.irrigated)}</div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Campo Observações</h3>
        <p className="comment-box">{policy.comments}</p>
      </div>
      <div className="mb-20">
        <h3>1 Coberturas e POS</h3>
        <h3>Cobertura Básica</h3>
        <div className="grid four-columns">
          <div className="bold">Cobertura</div>
          <div className="bold">Limite Máximo da Indenização</div>
          <div className="bold">Preço do Seguro Líquido</div>
          <div className="bold">POS</div>
          <div>
            Incêndio/raio; tromba d’água; alagamento/inundação; ventos fortes; ventos frios; granizo; chuva excessiva;
            seca; geada; variação excessiva de temperatura.
          </div>
          <div>{formatBraCurrency(policyData.basicSumInsured)}</div>
          <div>{formatBraCurrency(policyData.basicPremium)}</div>
          <div></div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Cobertura Adicional</h3>
        <div className="grid four-columns">
          <div className="bold">Replantio</div>
          <div className="bold">Limite Máximo da Indenização</div>
          <div className="bold">Preço do Seguro Líquido</div>
          <div className="bold">POS</div>
          <div>tromba d’água; granizo; e chuva excessiva.</div>
          <div>{formatBraCurrency(policyData.replantingSumInsured)}</div>
          <div>{formatBraCurrency(policyData.replantingPremium)}</div>
          <div></div>
        </div>
      </div>
      <div className="gt-report-page-separator-before" />
      <div className="gt-report-page-separator-after" />
      <div className="mb-20">
        <h3>2 Detalhamento do risco</h3>
      </div>
      {fieldLevelHarvests.map((harvest, index) => {
        const field = harvest.field_id ? fieldIndex[harvest.field_id] : null;
        const {
          basicSumInsured: harvestBasicSumInsured,
          replantingSumInsured: harvestReplantingSumInsured,
          basicPremium: harvestBasicPremium,
          replantingPremium: harvestReplantingPremium,
          areaHa,
        } = calculatePremiumDetails(
          [harvest],
          field ? [field] : [],
          policyData.sumInsuredPerArea,
          policyData.premiumRatePercent,
          farmLevelHarvestCustomColumns.mga?.replanting_premium_rate_percent ?? 0,
          policyCustomColumns.mga.coverage_type,
          policyCustomColumns.mga.federal_subsidy_percent ?? 0,
          policyCustomColumns.mga.state_subsidy_percent ?? 0,
        );
        const expectedPlantingDate = new HarvestCustomColumns(harvest.custom_columns).mga?.expected_planting_date;
        return (
          <>
            {index > 0 && (
              <>
                <div className="gt-report-page-separator-before" />
                <div className="gt-report-page-separator-after" />
              </>
            )}
            <div className="mb-20">
              <div className="grid four-columns four-columns-stripes emphasized-stripes">
                <div className="bold">Dados do Item</div>
                <div>{index + 1}</div>
                <div></div>
                <div></div>
                <div className="bold">Dados da propriedade</div>
                <div></div>
                <div></div>
                <div></div>
                <div>Nome da Propriedade</div>
                <div>{farm.farm_name}</div>
                <div></div>
                <div></div>
                <div>Endereço do Risco</div>
                <div>{farmCustomColumns.address?.street}</div>
                <div>Nº</div>
                <div>{farmCustomColumns.address?.number ?? 's/n'}</div>
                <div>Complemento</div>
                <div>{farmCustomColumns.address?.additional_information}</div>
                <div>Cidade</div>
                <div>{farmCustomColumns.address?.city}</div>
                <div>UF</div>
                <div>{farmCustomColumns.address?.state}</div>
                <div>CEP</div>
                <div>{formatBraZipcode(farmCustomColumns.address?.zip_code)}</div>
                <div className="bold">Características técnicas do Risco</div>
                <div></div>
                <div></div>
                <div></div>
                <div>Cultura</div>
                <div>{policyData.cropId}</div>
                <div>Contratação</div>
                <div>Área total</div>
                <div>Área Segurada (ha)</div>
                <div>{formatBraNumber(areaHa, false, true)}</div>
                <div>NC %</div>
                <div>{formatBraNumber(farmLevelHarvest.insured_percent, false, true)}</div>
                {policyCustomColumns.mga?.product_type === 'productivity' && (
                  <>
                    <div>Valor da Saca (R$/saca)</div>
                    <div>{formatBraNumber(policyData.commodityPricePerBag, false, true)}</div>
                  </>
                )}
                {policyCustomColumns.mga?.product_type === 'costing' && (
                  <>
                    <div>Valor do Custeio (R$/ha)</div>
                    <div>
                      {formatBraNumber(
                        calculateSumInsuredPerArea(
                          farmLevelHarvest.reference_yield,
                          farmLevelHarvest.commodity_price,
                          policyData.insuredYieldInBagsPerHa,
                        )?.val,
                        false,
                        true,
                      )}
                    </div>
                  </>
                )}
                <div></div>
                <div></div>
                <div>Produtividade Esperada (sc/ha)</div>
                <div>
                  {formatBraNumber(
                    convertYield('bags-per-hectare', farmLevelHarvest.reference_yield, null)?.val,
                    false,
                    true,
                  )}
                </div>
                <div>Produtividade Segurada (sc/ha)</div>
                <div>{formatBraNumber(policyData.insuredYieldInBagsPerHa, false, true)}</div>
                <div>Data prevista de plantio</div>
                <div>{expectedPlantingDate ? ` ${dateFormatter.format(new Date(expectedPlantingDate))}` : null}</div>
                <div>Grupo/Variedade</div>
                <div>{harvest.variety}</div>
                <div>Tipo de solo</div>
                <div>{formatSoilType(new HarvestCustomColumns(harvest.custom_columns).mga?.soil_type)}</div>
                <div>Água disponível</div>
                <div>{new HarvestCustomColumns(harvest.custom_columns).mga?.available_soil_water}</div>
                <div>IS Cobertura Básica</div>
                <div>{formatBraCurrency(harvestBasicSumInsured)}</div>
                <div>IS Cobertura Replantio</div>
                <div>{formatBraCurrency(harvestReplantingSumInsured)}</div>
                <div>Prêmio Líquido</div>
                <div>{formatBraCurrency(harvestBasicPremium + harvestReplantingPremium)}</div>
                <div></div>
                <div></div>

                <div className="bold">Coordenadas Geográficas</div>
                <div></div>
                <div></div>
                <div></div>
                <div>Latitude</div>
                <div>{field?.field_location?.[1]}</div>
                <div>Longitude</div>
                <div>{field?.field_location?.[0]}</div>
                <div>KML</div>
                <div>
                  <FieldShape
                    polygon={field?.field_shape!}
                    color={policyData.baseCrop ? CROP_COLORS[policyData.baseCrop] : undefined}
                  />
                </div>
                <div></div>
                <div></div>
              </div>
            </div>
          </>
        );
      })}
      <div className="mb-20">
        <h3>Vistorias</h3>
        <div className="grid four-columns four-columns-stripes">
          <div>Nome do acompanhante das vistorias</div>
          <div>{policyCustomColumns.representative?.name}</div>
          <div className="merge-bottom-border">CPF do acompanhante das vistorias</div>
          <div className="merge-bottom-border">
            {formatBraDocumentNumber(policyCustomColumns.representative?.external_id, 'person')}
          </div>
          <div>Telefone com DDD</div>
          <div>{formatBraPhoneNumber(policyCustomColumns.representative?.phone)}</div>
          <div className="merge-top-border"></div>
          <div className="merge-top-border"></div>
        </div>
      </div>
      <div className="mb-20">
        <h3>Roteiro de Acesso</h3>
        <div className="comment-box">{farmCustomColumns.address?.additional_information}</div>
      </div>
      <div className="gt-report-page-separator-before" />
      <div className="gt-report-page-separator-after" />
      {policyCustomColumns.beneficiaries?.map((beneficiary, index) => {
        return (
          <div className="mb-20">
            <h3>Cláusula Beneficiária</h3>
            <div className="grid three-columns">
              <div>Nome do Beneficiário</div>
              <div>CPF/CNPJ</div>
              <div>Participação (%)</div>
              <div>{beneficiary.entity.name}</div>
              <div>{formatBraDocumentNumber(beneficiary.entity.external_id, beneficiary.entity.type)}</div>
              <div>{beneficiary.percent}</div>
            </div>
          </div>
        );
      })}
      <div className="mb-20 justify-text">
        <h3>Observações</h3>
        {!policyExternalId && (
          <>
            <p>
              Após o recebimento da proposta, a seguradora dispõe de 15 (quinze) dias corridos para decidir sobre a
              aceitação ou recusa do risco. Para propostas subvencionadas, esse prazo é ampliado para 45 (quarenta e
              cinco) dias. É importante destacar que a aceitação da proposta de seguro está sujeita à análise do risco.
            </p>
          </>
        )}
        <p>
          É facultado ao Segurado, através do seu Corretor de Seguros, o direito de arrependimento em 07 (sete) dias da
          data de emissão da apólice conforme previsto no Código de Defesa do Consumidor, o que deverá ser feito
          mediante entrega de carta devidamente protocolada em qualquer escritório da Seguradora.
        </p>
        <p className="bold">
          Ressaltamos que, de acordo com a Resolução CNSP 382/2020, deverá ser dada ciência ao proponente, sobre as
          informações relativas ao produto e remuneração dos intermediários de seguros antes da aquisição do mesmo.
        </p>
      </div>
      {!policyExternalId && (
        <div className="mb-20 justify-text">
          <h3>Privacidade de Dados</h3>
          <p>
            Autorizo que a AXA SEGUROS S/A, as empresas do mesmo Grupo Econômico, seus fornecedores e/ou parceiros,
            procedam ao tratamento dos meus dados pessoais em atenção a celebração deste contrato, de acordo com a
            Política de Privacidade de Dados, disponível em{' '}
            <span className="link">https://www.axa.com.br/politica-deprivacidade/</span>. Mais detalhes sobre o
            tratamento de dados pessoais, estão disponíveis nas Condições Gerais deste produto.
          </p>
          <p>
            Em cumprimento à Circular 621 de 12.02.2021, o segurado, seu representante legal ou seu corretor de seguros,
            declara que tomou ciência das condições contratuais deste seguro e está de acordo com as mesmas.
          </p>
        </div>
      )}

      <div className="mb-20 justify-text">
        <h3>Informações Processo SUSEP</h3>
        {policyExternalId && (
          <p>
            SUSEP – Superintendência de Seguros Privados – Autarquia Federal responsável pela fiscalização, normatização
            e controle dosmercados de seguro, previdência complementar aberta, capitalização, resseguro e corretagem de
            seguros
          </p>
        )}

        <p>Condições do Seguro conforme processo(s) SUSEP</p>
        <div className="grid two-columns">
          <div>Processo SUSEP Principal</div>
          <div>
            {policyCustomColumns.mga.product_type === 'costing' ? '15414.900048/2016-09.' : '15414.652074/2021-74.'}
          </div>
        </div>

        {!policyExternalId && <p>A aceitação da proposta de seguro está sujeita à análise do risco.</p>}

        <p>
          O registro do produto é automático e não representa aprovação ou recomendação por parte da Susep; O segurado
          poderá consultar a situação cadastral do corretor de seguros e da sociedade seguradora no sítio eletrônico
          <span className="link">www.susep.gov.br</span>
        </p>

        {!policyExternalId && (
          <>
            <p>
              As condições contratuais/ regulamento deste produto encontram-se registradas na SUSEP de acordo com o(s)
              número(s) de processo(s) a seguir indicados e poderão ser consultadas no endereço eletrônico:
              <span className="link">www.susep.gov.br</span>.
            </p>
            <p>
              Importante registrar que além das Condições Gerais, somente as cláusulas e condições referentes às
              Coberturas Contratadas são aplicáveis ao seu seguro.
            </p>
          </>
        )}

        <p>
          O registro deste plano na SUSEP não implica, por parte da Autarquia, incentivo ou recomendação à sua
          comercialização. SUSEP – Superintendência de Seguros Privados – Autarquia Federal é a responsável pela
          fiscalização, normatização e controle dos mercados de seguro, previdência complementar aberta, capitalização,
          resseguro e corretagem de seguros.
        </p>

        <p>Número de telefone gratuito de atendimento ao público</p>
        <p>SUSEP: 0800 0218484 (atendimento exclusivo ao consumidor de todo Brasil)</p>

        {!policyExternalId && (
          <p>
            Estou ciente que a AXA Seguros poderá tomar decisões automatizadas, exclusivamente para fins de aceitação do
            seguro, com base nos dados fornecidos e nas regras de comercialização do produto e, ainda, que a Companhia
            não efetua o tratamento automatizado com fins discriminatórios e/ou ilícitos.
          </p>
        )}
      </div>

      {policyExternalId && <img style={{width: '50%'}} src="/mga/policy-signatures.png" />}

      {!policyExternalId && (
        <>
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
          <div className="mb-20 justify-text">
            <h3>Declaração</h3>
            <p>
              Declaro assumir total responsabilidade pela exatidão das informações e dados prestados, reconhecendo que
              quaisquer informações ou dados falsos, inverídicos, incompletos ou errôneos, importarão na perda do
              direito ao seguro e/ou ao bônus, nos termos dos artigos 765 e 766 do Código Civil, comprometendo-me ainda
              a comunicar à Seguradora quaisquer alterações com relação a informações e dados prestados em face do
              contrato de seguro que vier a ser celebrado.
            </p>
            <div className="signature"></div>
            <p>Assinatura do Segurado</p>
            {getAxaContactsSection()}
          </div>
        </>
      )}

      {policyData.stateSubsidy > 0 && farmCustomColumns.address?.state === SAO_PAULO_STATE_CODE && (
        <>
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
          <img src="/mga/sao-paolo-subsidy-attachment.png" />
        </>
      )}
      {policyData.federalSubsidy > 0 && (
        <>
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
          <img src="/mga/federal-subsidy-attachment-page-1.png" />
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
          <img src="/mga/federal-subsidy-attachment-page-2.png" />
          <div className="gt-report-page-separator-before" />
          <div className="gt-report-page-separator-after" />
          <img src="/mga/federal-subsidy-attachment-page-3.png" />
        </>
      )}
    </div>
  );
};
